"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Link } from "@/components/common/Link";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { EcommerceTermsProvider, useEcommerceTerms } from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
interface PaginationBarProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}
function PaginationBar({
  currentPage,
  totalPages,
  onPageChange
}: PaginationBarProps) {
  if (totalPages <= 1) return null;
  const getPageItems = (current: number, total: number): (number | string)[] => {
    if (total <= 7) {
      return Array.from({
        length: total
      }, (_, i) => i + 1);
    }
    const items: (number | string)[] = [];
    if (current <= 4) {
      for (let i = 1; i <= 5; i++) items.push(i);
      items.push("ellipsis");
      items.push(total);
    } else if (current >= total - 3) {
      items.push(1);
      items.push("ellipsis");
      for (let i = total - 4; i <= total; i++) items.push(i);
    } else {
      items.push(1);
      items.push("ellipsis-1");
      items.push(current - 1);
      items.push(current);
      items.push(current + 1);
      items.push("ellipsis-2");
      items.push(total);
    }
    return items;
  };
  return <div className="rounded-lg border border-border bg-card p-3 md:p-4 shadow-sm">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center justify-between sm:justify-start gap-2">
          <Button type="button" variant="outline" size="default" onClick={() => onPageChange(Math.max(1, currentPage - 1))} disabled={currentPage <= 1} aria-label="Página anterior" className="h-10 px-3 md:px-4 font-default text-xs tracking-wider uppercase border-border/80 hover:border-primary hover:text-primary disabled:opacity-40">
            <ChevronLeft className="size-4 mr-1 sm:mr-1.5" />
            <span className="hidden sm:inline">Anterior</span>
          </Button>

          <div className="flex items-center gap-1.5">
            {getPageItems(currentPage, totalPages).map((item, idx) => {
            if (typeof item === "string") {
              return <span key={`ell-${idx}`} className="px-2 text-sm text-muted-foreground select-none" aria-hidden="true">
                    …
                  </span>;
            }
            const isCurrent = item === currentPage;
            return <Button key={item} type="button" variant={isCurrent ? "default" : "outline"} onClick={() => onPageChange(item)} aria-label={`Ir a la página ${item}`} aria-current={isCurrent ? "page" : undefined} className={`size-10 min-w-10 rounded-md font-default text-sm font-semibold transition-colors ${isCurrent ? "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 border-transparent" : "border-border/80 text-foreground hover:border-primary hover:text-primary hover:bg-accent"}`}>
                  {item}
                </Button>;
          })}
          </div>

          <Button type="button" variant="outline" size="default" onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))} disabled={currentPage >= totalPages} aria-label="Página siguiente" className="h-10 px-3 md:px-4 font-default text-xs tracking-wider uppercase border-border/80 hover:border-primary hover:text-primary disabled:opacity-40">
            <span className="hidden sm:inline">Siguiente</span>
            <ChevronRight className="size-4 ml-1 sm:ml-1.5" />
          </Button>
        </div>

        <div className="text-center sm:text-right">
          <span className="text-xs font-default uppercase tracking-wider text-muted-foreground">
            Página <strong className="text-foreground font-semibold">{currentPage}</strong> de{" "}
            <strong className="text-foreground font-semibold">{totalPages}</strong>
          </span>
        </div>
      </div>
    </div>;
}
interface CategoryFilterButtonsProps {
  activeCategory: string;
  onSelectCategory: (category: {
    slug: string;
    id: number | null;
  }) => void;
}
function CategoryFilterButtons({
  activeCategory,
  onSelectCategory
}: CategoryFilterButtonsProps) {
  const {
    terms,
    loading
  } = useEcommerceTerms();
  const categories = React.useMemo(() => {
    const list: Array<{
      label: string;
      value: string;
      id: number | null;
    }> = [{
      label: "TODO",
      value: "all",
      id: null
    }];
    terms.forEach(term => {
      // Exclude uncategorized if present or empty counts if count is 0
      if (term.count === 0) return;
      if (term.slug === "uncategorized" || term.slug === "sin-categorizar") return;
      const numId = Number(term.id);
      list.push({
        label: term.name.toUpperCase(),
        value: term.slug || String(term.id),
        id: !isNaN(numId) ? numId : null
      });
    });
    return list;
  }, [terms]);
  return <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
      <div className="flex items-center gap-2 md:flex-wrap">
        {categories.map((cat, i) => <Button key={cat.value} data-index={i} variant="ghost" onClick={() => onSelectCategory({
        slug: cat.value,
        id: cat.id
      })} className={`shrink-0 h-auto px-4 py-2 text-[11px] tracking-[0.2em] font-default border rounded-md transition-colors ${activeCategory === cat.value ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90" : "border-border/60 text-foreground/70 hover:border-primary hover:text-primary hover:bg-accent"}`}>
            {cat.label}
          </Button>)}
      </div>
    </div>;
}
function ProductsCatalog() {
  const {
    posts: products,
    loading,
    error,
    refetch,
    setSorting,
    currentPage,
    totalPages,
    setPage,
    total,
    setWPQuery
  } = useEcommerceProducts() as any;
  const [activeCategory, setActiveCategory] = React.useState("all");
  const [activeCategoryId, setActiveCategoryId] = React.useState<number | null>(null);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedProduct, setSelectedProduct] = React.useState<any>(null);
  const setWPQueryRef = React.useRef(setWPQuery);
  React.useEffect(() => {
    setWPQueryRef.current = setWPQuery;
  });
  const activeCategoryIdRef = React.useRef(activeCategoryId);
  React.useEffect(() => {
    activeCategoryIdRef.current = activeCategoryId;
  }, [activeCategoryId]);
  const searchTermRef = React.useRef(searchTerm);
  React.useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);
  const isInitialMount = React.useRef(true);
  React.useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const timer = setTimeout(() => {
      if (typeof setWPQueryRef.current === "function") {
        setWPQueryRef.current((prev: WP_Query | null) => {
          const currentVars = {
            ...(prev?.query_vars ?? {})
          };
          if (searchTerm) {
            currentVars.s = searchTerm;
          } else {
            delete currentVars.s;
          }
          currentVars.paged = 1;
          const newQ = new WP_Query(currentVars);
          if (prev?.meta_query) newQ.meta_query = prev.meta_query;
          if (prev?.date_query) newQ.date_query = prev.date_query;
          if (activeCategoryIdRef.current !== null) {
            newQ.tax_query = [{
              taxonomy: "product_cat",
              field: "term_id",
              terms: [activeCategoryIdRef.current],
              operator: "IN"
            }];
          } else {
            newQ.tax_query = null;
          }
          return newQ;
        });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);
  const handleSelectCategory = ({
    slug,
    id
  }: {
    slug: string;
    id: number | null;
  }) => {
    setActiveCategory(slug);
    setActiveCategoryId(id);
    setWPQuery((prev: WP_Query | null) => {
      const vars = {
        ...(prev?.query_vars ?? {})
      };
      vars.paged = 1;
      const newQuery = new WP_Query(vars);
      if (prev?.meta_query) newQuery.meta_query = prev.meta_query;
      if (prev?.date_query) newQuery.date_query = prev.date_query;
      if (slug === "all" || id === null) {
        newQuery.tax_query = null;
      } else {
        newQuery.tax_query = [{
          taxonomy: "product_cat",
          field: "term_id",
          terms: [id],
          operator: "IN"
        }];
      }
      return newQuery;
    });
  };
  const handleSort = (value: string) => {
    if (value === "newest") setSorting("date", "desc");else if (value === "oldest") setSorting("date", "asc");else if (value === "price-asc") setSorting("price", "asc");else if (value === "price-desc") setSorting("price", "desc");else if (value === "name") setSorting("title", "asc");
  };
  const handlePageChange = (page: number) => {
    setPage(page);
    document.getElementById("shop-content")?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  };
  return <>
      {/* Search Input */}
      <div className="mb-6">
        <div className="relative w-full">
          <Search className="absolute left-4 top-1/2 size-5 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input type="search" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar en todo el catálogo de productos..." className="h-14 w-full rounded-lg! border-border/60! bg-card pl-12! pr-12! py-1! text-base! md:text-lg! font-default shadow-xs focus-visible:border-primary [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden" />
          {searchTerm && <Button type="button" variant="ghost" size="icon" onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 size-8 text-muted-foreground hover:text-foreground" aria-label="Limpiar búsqueda">
              <X className="size-4" />
            </Button>}
        </div>
      </div>

      {/* Filter and Sort Toolbar */}
      <div className="border-y border-border/40">
        <div className="flex flex-col gap-4 py-5 md:flex-row md:items-center md:justify-between">
          <CategoryFilterButtons activeCategory={activeCategory} onSelectCategory={handleSelectCategory} />

          <div className="flex items-center gap-3">
            <Select onValueChange={handleSort} defaultValue="newest">
              <SelectTrigger className="h-9 border-border/60 bg-transparent text-[11px] tracking-[0.2em] font-default uppercase">
                <SelectValue placeholder="Ordenar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Más recientes</SelectItem>
                <SelectItem value="oldest">Más antiguos</SelectItem>
                <SelectItem value="price-asc">Precio: Menor a Mayor</SelectItem>
                <SelectItem value="price-desc">Precio: Mayor a Menor</SelectItem>
                <SelectItem value="name">Nombre</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Products & Pagination */}
      <div className="mt-12">
        {totalPages > 1 && <div className="mb-8">
            <PaginationBar currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          </div>}

        {loading ? <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-4">
            {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="aspect-square bg-muted/20 animate-pulse rounded-lg" />)}
          </div> : error ? <div className="py-16 text-center">
            <p className="text-sm text-muted-foreground font-default tracking-wider">
              Error al conectar con el catálogo de productos
            </p>
            <Button onClick={refetch} variant="ghost" className="mt-4 text-xs tracking-[0.2em] font-default uppercase underline">
              Reintentar
            </Button>
          </div> : products.length === 0 ? <div className="py-24 text-center">
            <p className="font-default text-2xl text-foreground/70">
              No se encontraron productos.
            </p>
            <p className="mt-2 text-[11px] tracking-[0.2em] font-default uppercase text-muted-foreground">
              Intenta ajustar los filtros o el término de búsqueda
            </p>
          </div> : <div className="grid grid-cols-2 gap-6 md:grid-cols-3 md:gap-8 lg:grid-cols-4">
            {products.map((product: any, i: number) => <article key={product.id} data-index={i} className="group relative">
                <div className="relative overflow-hidden bg-card shadow-md rounded-lg transition-transform duration-500 group-hover:-translate-y-1">
                  <Link to={product.link}>
                    <div className="relative aspect-square overflow-hidden p-4 md:p-6">
                      {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" /> : <div className="flex h-full w-full items-center justify-center bg-muted/30">
                          <span className="font-default text-4xl text-foreground/20">
                            VOZ
                          </span>
                        </div>}
                    </div>
                  </Link>

                  <div className="pointer-events-none absolute inset-x-4 bottom-4 translate-y-4 opacity-0 transition-all duration-300 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 md:inset-x-6 md:bottom-6">
                    <Link to={product.link}>
                      <Button variant="default" className="w-full bg-primary text-primary-foreground text-[11px] tracking-[0.25em] font-default uppercase hover:bg-primary/90 rounded-md">
                        Vista Rápida
                      </Button>
                    </Link>
                  </div>
                </div>

                <div className="mt-4 px-1">
                  {product.categories && product.categories.length > 0 && <p className="text-[10px] tracking-[0.25em] font-default uppercase text-muted-foreground">
                      {product.categories[0].name}
                    </p>}
                  <Link to={product.link}>
                    <h3 className="mt-2 font-default text-base leading-snug text-foreground line-clamp-2 transition-colors hover:text-primary md:text-lg">
                      {product.title}
                    </h3>
                  </Link>
                </div>
              </article>)}
          </div>}

        {total > 0 && <p className="mt-8 text-center text-[10px] tracking-[0.25em] font-default uppercase text-muted-foreground">
            Mostrando {products.length} de {total} productos
          </p>}

        {totalPages > 1 && <div className="mt-8">
            <PaginationBar currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          </div>}
      </div>

      {/* Quick View Dialog */}
      <Dialog open={!!selectedProduct} onOpenChange={open => !open && setSelectedProduct(null)}>
        <DialogContent showCloseButton={false} className="max-w-4xl border-border/40 bg-background p-0 rounded-lg">
          {selectedProduct && <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="relative aspect-square bg-card p-6 md:p-10">
                {selectedProduct.featured_image_url ? <img src={selectedProduct.featured_image_url} alt={selectedProduct.title} loading="lazy" className="h-full w-full object-cover rounded-lg" /> : <div className="flex h-full w-full items-center justify-center bg-muted/30">
                    <span className="font-default text-6xl text-foreground/20">
                      VOZ
                    </span>
                  </div>}
              </div>

              <div className="flex flex-col p-6 md:p-10">
                <div className="flex items-start justify-between">
                  {selectedProduct.categories && selectedProduct.categories.length > 0 ? <p className="text-[10px] tracking-[0.3em] font-default uppercase text-muted-foreground">
                      {selectedProduct.categories[0].name}
                    </p> : <span />}
                  <Button variant="ghost" size="icon" onClick={() => setSelectedProduct(null)} className="h-8 w-8 text-foreground/60 hover:text-foreground hover:bg-accent rounded-md" aria-label="Cerrar">
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <DialogHeader className="mt-4">
                  <DialogTitle className="font-default text-2xl leading-tight text-foreground md:text-3xl">
                    {selectedProduct.title}
                  </DialogTitle>
                </DialogHeader>

                <p className="mt-4 text-sm tracking-[0.2em] font-default uppercase text-primary">
                  {selectedProduct.discounted_price ? <>
                      <span>${selectedProduct.discounted_price.toFixed(2)}</span>
                      <span className="ml-2 text-muted-foreground line-through">
                        ${selectedProduct.price.toFixed(2)}
                      </span>
                    </> : <span>${selectedProduct.price?.toFixed(2)}</span>}
                </p>

                {selectedProduct.short_description && <div className="mt-6 font-default text-sm leading-relaxed text-foreground/80 [&_p]:mb-2" dangerouslySetInnerHTML={{
              __html: selectedProduct.short_description
            }} />}

                <div className="mt-6 space-y-3 border-t border-border/40 pt-6">
                  <div className="flex justify-between text-[11px] tracking-[0.2em] font-default uppercase">
                    <span className="text-muted-foreground">Marca</span>
                    <span className="text-foreground">
                      {selectedProduct.customFields?.brand || "Grandstream"}
                    </span>
                  </div>
                  <div className="flex justify-between text-[11px] tracking-[0.2em] font-default uppercase">
                    <span className="text-muted-foreground">Conectividad</span>
                    <span className="text-foreground">
                      {selectedProduct.customFields?.connectivity || "IP / SIP"}
                    </span>
                  </div>
                  <div className="flex justify-between text-[11px] tracking-[0.2em] font-default uppercase">
                    <span className="text-muted-foreground">Garantía</span>
                    <span className="text-foreground">12 Meses</span>
                  </div>
                </div>

                <div className="mt-auto pt-8">
                  <AddToCartButton productId={selectedProduct.id} className="w-full" buttonClassName="bg-primary text-primary-foreground w-full rounded-md h-12 text-[11px] tracking-[0.3em] font-default uppercase hover:bg-primary/90" text="Solicitar Cotización" showQuantity={true} />
                  <Link to={selectedProduct.link} className="mt-4 block text-center text-[11px] tracking-[0.25em] font-default uppercase text-foreground/60 underline underline-offset-4 hover:text-primary">
                    Ver Detalles
                  </Link>
                </div>
              </div>
            </div>}
        </DialogContent>
      </Dialog>
    </>;
}
export default function Catalog() {
  return <section data-section-id="3534" id="shop-content" className="bg-background text-foreground scroll-mt-24 ![padding:0px_0px_128px_0px]">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="mt-12 md:mt-16">
          <EcommerceTermsProvider taxonomy="product_cat" wp_term_query={{
          orderby: "name",
          order: "asc",
          per_page: 100
        }}>
            <EcommerceProductsProvider wp_query={{
            per_page: 12,
            order: "desc",
            orderby: "date"
          }}>
              <ProductsCatalog />
            </EcommerceProductsProvider>
          </EcommerceTermsProvider>
        </div>
      </div>
    </section>;
}